import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import Button from '../components/ui/Button';
import NetworkVisual from '../components/visuals/NetworkVisual';

const WEB3FORMS_ACCESS_KEY = '0ed662df-e830-407b-be04-2122aeb7079b';

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData,
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Unable to send your message. Please try again.');
      }

      setIsSubmitted(true);
      form.reset();
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      setSubmitError(error.message || 'Unable to send your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCalendly = (e) => {
    if (e) e.preventDefault();
    if (window.Calendly) {
      window.Calendly.initPopupWidget({ url: 'https://calendly.com/prboost7/30-minutes-meeting' });
    }
  };

  return (
    <div className="bg-[#0B0F19] min-h-screen">
      <section className="relative pt-40 pb-24 overflow-hidden border-b border-white/5">
        <NetworkVisual />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl h-[400px] bg-blue-600/10 blur-[150px] rounded-full pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center flex flex-col items-center mb-16">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight tracking-tight"
            >
              Get in <span className="text-blue-500">Touch</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ delay: 0.1 }}
              className="text-xl text-slate-400 leading-relaxed max-w-2xl mx-auto"
            >
              Have questions about our services or want to discuss a custom campaign? Our team is ready to help you scale your authority.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-5 gap-12 max-w-6xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, x: -20 }} 
              animate={{ opacity: 1, x: 0 }} 
              className="md:col-span-2 space-y-8"
            >
              <div className="glass-card p-8 rounded-3xl border-white/5 bg-[#12151e]">
                <h3 className="text-2xl font-bold text-white mb-6">Contact Information</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center flex-shrink-0 border border-blue-500/20">
                      <SafeIcon icon={FiIcons.FiMail} className="text-blue-400 text-xl" />
                    </div>
                    <div>
                      <h4 className="text-white font-medium mb-1 text-lg">Email Us</h4>
                      <p className="text-slate-400 text-sm">support@prboost.com</p>
                      <p className="text-slate-400 text-sm">sales@prboost.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0 border border-emerald-500/20">
                      <SafeIcon icon={FiIcons.FiCalendar} className="text-emerald-400 text-xl" />
                    </div>
                    <div>
                      <h4 className="text-white font-medium mb-1 text-lg">Book a Demo</h4>
                      <p className="text-slate-400 text-sm">Schedule a 1-on-1 walkthrough.</p>
                      <button 
                        onClick={openCalendly}
                        className="text-emerald-400 text-sm font-bold uppercase tracking-wider mt-2 hover:text-emerald-300 transition-colors flex items-center gap-1.5 focus:outline-none"
                      >
                        Find a time <SafeIcon icon={FiIcons.FiChevronRight} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              animate={{ opacity: 1, x: 0 }} 
              className="md:col-span-3"
            >
              <div className="glass-card p-8 md:p-12 rounded-3xl border-white/10 bg-[#12151e] relative overflow-hidden">
                {isSubmitted && (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }} 
                    animate={{ opacity: 1, scale: 1 }}
                    className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-[#12151e]/95 backdrop-blur-sm"
                  >
                    <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mb-6 border border-emerald-500/30">
                      <SafeIcon icon={FiIcons.FiCheck} className="text-4xl text-emerald-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">Message Sent!</h3>
                    <p className="text-slate-400 text-center max-w-sm">
                      Thank you for reaching out. A member of our team will get back to you shortly.
                    </p>
                  </motion.div>
                )}

                <form action="https://api.web3forms.com/submit" method="POST" onSubmit={handleSubmit} className="space-y-6 relative z-10">
                  <input type="hidden" name="access_key" value={WEB3FORMS_ACCESS_KEY} />
                  <input type="hidden" name="from_name" value="PRBoost Website" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-300 uppercase tracking-widest">Name <span className="text-red-500">*</span></label>
                      <input 
                        type="text" 
                        name="name" 
                        required 
                        className="w-full bg-[#0B0F19] border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all placeholder:text-slate-400" 
                        placeholder="John Doe" 
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-300 uppercase tracking-widest">Email Address <span className="text-red-500">*</span></label>
                      <input 
                        type="email" 
                        name="email"
                        required 
                        className="w-full bg-[#0B0F19] border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all placeholder:text-slate-400" 
                        placeholder="john@company.com" 
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-300 uppercase tracking-widest">Subject</label>
                    <div className="relative">
                      <select 
                        name="subject"
                        defaultValue=""
                        className="w-full bg-[#0B0F19] border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all appearance-none"
                      >
                        <option value="" disabled>Select a service</option>
                        <option value="guest-posts">Guest Posts</option>
                        <option value="press-releases">Press Releases</option>
                        <option value="link-insertions">Link Insertions</option>
                        <option value="reddit">Reddit Marketing</option>
                        <option value="quora">Quora Marketing</option>
                        <option value="youtube">YouTube SEO</option>
                        <option value="google-maps">Google Maps SEO</option>
                        <option value="product-reviews">Product Reviews</option>
                        <option value="brand-mentions">Brand Mentions</option>
                        <option value="other">Other / General Inquiry</option>
                      </select>
                      <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-slate-400">
                        <SafeIcon icon={FiIcons.FiChevronDown} />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-300 uppercase tracking-widest">Message <span className="text-red-500">*</span></label>
                    <textarea 
                      name="message"
                      required 
                      rows="5" 
                      className="w-full bg-[#0B0F19] border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all resize-none placeholder:text-slate-400" 
                      placeholder="Tell us how we can help you..." 
                    ></textarea>
                  </div>

                  {submitError && (
                    <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-medium">
                      {submitError}
                    </div>
                  )}

                  <div className="pt-2">
                    <Button 
                      type="submit"
                      variant="primary" 
                      disabled={isSubmitting}
                      className={`w-full !py-4 text-lg font-bold flex items-center justify-center gap-2 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {isSubmitting ? (
                        <>
                          <SafeIcon icon={FiIcons.FiLoader} className="animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <SafeIcon icon={FiIcons.FiSend} className="text-lg" />
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
